/* eslint-disable no-console */
import React, { useRef, useState, useEffect } from 'react';
import {
  ActivaPassivaCard,
  ActivaPassivaSummary,
  CustomCurrencyInput,
  Hr,
  IconWithTitle,
  LabelWithTooltip,
  OnroerendeIcon,
  responsivePadding,
  RoerendIcon,
  SchuldenIcon,
  useOnScreen,
  YesNoToggle,
} from '@myfaro/ui-react';
import { Box, Grid, Typography, Select, MenuItem } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { BalanceSheet, Inventory } from '@myfaro/domain';
import { useProfileContext } from '../../core/profile';
import {
  MovablePropertyType,
  What as WhatClass,
  ImmovablePropertyType,
} from '../../core/types';
import { formatValuta } from '../../helpers';
import config from '../../core/context/config/config';

const { debtFields } = config;

const useStyles = makeStyles(theme => ({
  body: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    paddingTop: theme.spacing(4),
    ...responsivePadding(theme),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  subTitle: {
    marginBottom: theme.spacing(4),
  },
  input: {
    marginBottom: theme.spacing(2),
  },
  inputLabel: {
    marginBottom: theme.spacing(1),
  },
  activaPassivaContainer: {
    backgroundColor: theme.palette.background.paper,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    ...responsivePadding(theme),
  },
  toggle: {
    marginBottom: theme.spacing(1),
  },
  select: {
    width: '100%',
  },
  summary: {
    position: 'fixed',
    left: '50%',
    bottom: '5%',
    transform: 'translateX(-50%)',
    zIndex: 1,

    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
}));

const { immovableProperties, movableProperties } = config;

interface Amount {
  label: string;
  amount: number;
}

function What() {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const classes = useStyles();
  const activaPassivaRef = useRef();
  const activaPassivaVisible = useOnScreen(activaPassivaRef);
  const { useProfile, onChange, fieldError } = useProfileContext();
  const data = useProfile<WhatClass>('what');
  const onChangeWhat = onChange('what');
  const { balanceSheet } = data;

  const [isTouched, setIsTouched] = useState(() => {
    return {
      bankSavingsValue: false,
      bankInvestmentsValue: false,
      insuranceProtectionValue: false,
      insuranceInvestmentsValue: false,
      savingsInvestmentContracts: false,
      businessAssetsValue: false,
      homeValue: false,
      otherRealEstateValue: false,
      liabilitiesValue: false,
      liabilitiesDurationInYears: false,
      liabilitiesRemainingAtRetirement: false,
      monthlyExpensesLiabilitiesValue: false,
    };
  });

  const activa: Amount[] = [
    {
      label: t('what.bankSavingsValue'),
      amount: balanceSheet?.bankSavingsValue ?? 0,
    },
    {
      label: t('what.bankInvestmentsValue'),
      amount: balanceSheet?.bankInvestmentsValue ?? 0,
    },
    {
      label: t('what.insuranceProtectionValue'),
      amount: balanceSheet?.insuranceProtectionValue ?? 0,
    },
    {
      label: t('what.businessAssetsValue'),
      amount: balanceSheet?.businessAssetsValue ?? 0,
    },
    {
      label: t('what.ownHouse'),
      amount: balanceSheet?.homeValue ?? 0,
    },
    {
      label: t('what.otherThanOwnHouse'),
      amount: balanceSheet?.otherRealEstateValue ?? 0,
    },
    {
      label: t('what.insuranceInvestmentsValue'),
      amount: balanceSheet?.insuranceInvestmentsValue ?? 0,
    },
  ];
  let activaTotal = activa.reduce((a, b) => a + b.amount, 0);
  const passiva: Amount[] = [
    {
      label: t('label.liabilitiesValue'),
      amount: balanceSheet?.liabilitiesValue ?? 0,
    },
  ];
  let passivaTotal = passiva.reduce((a, b) => a + b.amount, 0);

  if (activaTotal > passivaTotal) {
    passiva.push({
      label: t('what.netCapital'),
      amount: activaTotal - passivaTotal,
    });
    passivaTotal += activaTotal - passivaTotal;
  } else if (passivaTotal > activaTotal) {
    activa.push({
      label: t('what.netDebt'),
      amount: passivaTotal - activaTotal,
    });
    activaTotal += passivaTotal - activaTotal;
  }

  return (
    <>
      <Box {...({ ref: containerRef } as any)} className={classes.body}>
        {!activaPassivaVisible && (
          <Box className={classes.summary}>
            <ActivaPassivaSummary
              passivaItem={{
                label: t('label.passiva'),
                amount: formatValuta(passivaTotal),
              }}
              activaItem={{
                label: t('label.activa'),
                amount: formatValuta(activaTotal),
              }}
            />
          </Box>
        )}
        <Typography variant='h5' className={classes.title}>
          {t('what.title')}
        </Typography>
        <Typography
          variant='body2'
          color={'textSecondary'}
          className={classes.subTitle}>
          {t('what.subTitle')}
        </Typography>
        <IconWithTitle
          icon={<RoerendIcon />}
          title={t('label.movableProperty')}
        />
        <Grid container spacing={2}>
          {movableProperties.map(
            ({ label, name, tooltipTitle }: MovablePropertyType) => {
              return (
                <Grid key={name} item xs={12} md={6}>
                  <CustomCurrencyInput
                    label={t(label)}
                    value={balanceSheet?.[name] ?? ''}
                    tooltipTitle={t(tooltipTitle)}
                    onBlur={() =>
                      !isTouched[name] &&
                      setIsTouched(state => ({ ...state, [name]: true }))
                    }
                    onChange={(value: number) => {
                      onChangeWhat({
                        balanceSheet: {
                          ...balanceSheet,
                          [name]: value,
                        },
                      });
                    }}
                  />
                </Grid>
              );
            },
          )}
        </Grid>
        <Hr />

        <IconWithTitle
          icon={<OnroerendeIcon />}
          title={t('label.realEstate')}
        />

        <Grid container spacing={2}>
          {immovableProperties.map(({ name, label, tooltipTitle }) => (
            <Grid key={name} item xs={12} md={6}>
              <CustomCurrencyInput
                label={t(label)}
                value={balanceSheet?.[name] ?? ''}
                tooltipTitle={t(tooltipTitle)}
                onBlur={() => {
                  if (!isTouched[name]) {
                    setIsTouched(state => ({
                      ...state,
                      [name]: true,
                    }));
                  }
                }}
                onChange={(value: number) =>
                  onChangeWhat({
                    balanceSheet: {
                      ...balanceSheet,
                      [name]: value,
                    },
                  })
                }
              />
            </Grid>
          ))}
        </Grid>

        <Hr />

        <IconWithTitle icon={<SchuldenIcon />} title={t('label.debt')} />

        <Grid container spacing={2}>
          {debtFields.includes('liabilitiesValue') && (
            <Grid item xs={12} md={6}>
              <CustomCurrencyInput
                label={t('label.liabilitiesValue')}
                value={balanceSheet?.liabilitiesValue ?? ''}
                tooltipTitle={t('tooltip.liabilitiesValue')}
                onBlur={() => {
                  if (!isTouched.liabilitiesValue) {
                    setIsTouched(state => ({
                      ...state,
                      liabilitiesValue: true,
                    }));
                  }
                }}
                onChange={(value: number) =>
                  onChangeWhat({
                    balanceSheet: {
                      ...balanceSheet,
                      liabilitiesValue: value,
                    },
                  })
                }
              />
            </Grid>
          )}

          {debtFields.includes('liabilitiesRemainingAtRetirementToggle') && (
            <Grid item xs={12} md={6}>
              <LabelWithTooltip
                label={t('label.liabilitiesRemainingAtRetirmentToggle')}
                tooltipTitle={t(
                  'tooltip.liabilitiesRemainingAtRetirmentToggle',
                )}
              />
              <YesNoToggle
                labels={{ yes: t('label.yes'), no: t('label.no') }}
                selected={!!balanceSheet?.liabilitiesRemainingAtRetirement}
                className={classes.toggle}
                onChange={(value: any) => {
                  // TODO find correct field
                  onChangeWhat({
                    balanceSheet: {
                      ...balanceSheet,
                      liabilitiesRemainingAtRetirement: value
                        ? balanceSheet?.liabilitiesValue
                        : 0,
                    },
                  });
                }}
              />
            </Grid>
          )}
          {debtFields.includes('liabilitiesRemainingAtRetirementInput') && (
            <Grid item xs={12} md={6}>
              <CustomCurrencyInput
                label={t('label.liabilitiesRemainingAtRetirement')}
                value={balanceSheet?.liabilitiesRemainingAtRetirement ?? ''}
                tooltipTitle={t('tooltip.liabilitiesRemainingAtRetirement')}
                onBlur={() => {
                  if (!isTouched.liabilitiesRemainingAtRetirement) {
                    setIsTouched(state => ({
                      ...state,
                      liabilitiesRemainingAtRetirement: true,
                    }));
                  }
                }}
                onChange={(value: number) =>
                  onChangeWhat({
                    balanceSheet: {
                      ...balanceSheet,
                      liabilitiesRemainingAtRetirement: value,
                    },
                  })
                }
              />
            </Grid>
          )}
          {debtFields.includes('monthlyExpensesLiabilitiesValue') && (
            <Grid item xs={12} md={6}>
              <CustomCurrencyInput
                label={t('label.monthlyExpensesLiabilitiesValue')}
                value={balanceSheet?.monthlyExpensesLiabilitiesValue}
                tooltipTitle={t('tooltip.monthlyExpensesLiabilitiesValue')}
                onBlur={() => {
                  if (!isTouched.monthlyExpensesLiabilitiesValue) {
                    setIsTouched(state => ({
                      ...state,
                      monthlyExpensesLiabilitiesValue: true,
                    }));
                  }
                }}
                onChange={(value: number) =>
                  onChangeWhat({
                    balanceSheet: {
                      ...balanceSheet,
                      monthlyExpensesLiabilitiesValue: value,
                    },
                  })
                }
              />
              <Select
                variant='outlined'
                className={classes.select}
                labelId='demo-simple-select-label'
                id='demo-simple-select'
                value={balanceSheet?.liabilitiesDurationInYears}
                onChange={event =>
                  onChangeWhat({
                    balanceSheet: {
                      ...balanceSheet,
                      liabilitiesDurationInYears: event.target
                        .value as BalanceSheet['liabilitiesDurationInYears'],
                    },
                  })
                }
                onBlur={() =>
                  !isTouched.liabilitiesDurationInYears &&
                  setIsTouched(state => ({
                    ...state,
                    liabilitiesDurationInYears: true,
                  }))
                }
                error={
                  isTouched.liabilitiesDurationInYears &&
                  fieldError('what', 'liabilitiesDurationInYears')
                }>
                {Array(20)
                  .fill(null)
                  .map((num, i) => (
                    <MenuItem key={num} value={i + 1}>
                      {i + 1} {i > 0 ? t('label.years') : t('label.year')}
                    </MenuItem>
                  ))}
              </Select>
            </Grid>
          )}
        </Grid>
        <Hr />
      </Box>

      <Box
        {...({ ref: activaPassivaRef } as any)}
        className={classes.activaPassivaContainer}>
        <ActivaPassivaCard
          headerTitle={t('label.overview')}
          activaTitle={t('label.activa')}
          activaItems={activa
            .filter(a => a.amount !== 0)
            .map(a => {
              return { label: a.label, amount: formatValuta(a.amount) };
            })}
          activaTotal={formatValuta(activaTotal)}
          passivaTitle={t('label.passiva')}
          passivaItems={passiva
            .filter(a => a.amount !== 0)
            .map(a => {
              return { label: a.label, amount: formatValuta(a.amount) };
            })}
          passivaTotal={formatValuta(passivaTotal)}
        />
      </Box>
    </>
  );
}

export default What;
